import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Search, Plus, Eye, Send, Edit, Trash2 } from "lucide-react";
import ExpenseStatusBadge from "./ExpenseStatusBadge";
import MobileExpenseCard from "./MobileExpenseCard";
import { Currency } from "@/components/i18n/Currency";
import { LocalDate } from "@/components/i18n/LocalDate";
import expenseService from "@/services/expenseService";
import {
  Expense,
  ExpenseStatus,
  ExpenseCategory,
  ExpenseFilters,
  EXPENSE_CATEGORY_INFO,
} from "@/types/expense";

interface ContractorExpenseListProps {
  onCreateExpense?: () => void;
  onViewExpense?: (expense: Expense) => void;
  onEditExpense?: (expense: Expense) => void;
  statusFilters?: ExpenseStatus[]; // External status filters from parent
  onClearFilters?: () => void; // Callback to clear external filters
  className?: string;
}

const ITEMS_PER_PAGE = 10;

const ContractorExpenseList = React.forwardRef<
  HTMLDivElement,
  ContractorExpenseListProps
>(
  (
    {
      onCreateExpense,
      onViewExpense,
      onEditExpense,
      statusFilters,
      onClearFilters,
      className,
    },
    ref,
  ) => {
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Filters
    const [filters, setFilters] = useState<ExpenseFilters>({});
    const [searchTerm, setSearchTerm] = useState("");
    const [statusFilter, setStatusFilter] = useState<ExpenseStatus | "all">(
      "all",
    );
    const [categoryFilter, setCategoryFilter] = useState<
      ExpenseCategory | "all"
    >("all");

    const { toast } = useToast();

    // Load expenses
    const loadExpenses = async (page = 1) => {
      setLoading(true);
      try {
        // Use external status filters if provided, otherwise use internal filter
        const effectiveStatusFilter = statusFilters?.length
          ? statusFilters
          : statusFilter !== "all"
            ? [statusFilter as ExpenseStatus]
            : undefined;

        const filterParams = {
          ...filters,
          page,
          pageSize: ITEMS_PER_PAGE,
          search: searchTerm || undefined,
          status: effectiveStatusFilter,
          category:
            categoryFilter !== "all"
              ? [categoryFilter as ExpenseCategory]
              : undefined,
        };

        const response =
          await expenseService.getContractorExpenses(filterParams);

        setExpenses(response.expenses);
        setTotalCount(response.totalCount);
        setCurrentPage(response.page);
        setTotalPages(response.totalPages);
      } catch (error: any) {
        console.error("Error loading expenses:", error);
        toast({
          title: "Error",
          description: "Failed to load expenses. Please try again.",
          variant: "destructive",
        });
        setExpenses([]);
      } finally {
        setLoading(false);
      }
    };

    // Effect to load expenses when filters change
    useEffect(() => {
      loadExpenses(1);
    }, [searchTerm, statusFilter, categoryFilter, filters, statusFilters]);

    // Handle search
    const handleSearch = (value: string) => {
      setSearchTerm(value);
      setCurrentPage(1);
    };

    // Handle status filter
    const handleStatusFilter = (value: string) => {
      setStatusFilter(value as ExpenseStatus | "all");
      setCurrentPage(1);
    };

    // Handle category filter
    const handleCategoryFilter = (value: string) => {
      setCategoryFilter(value as ExpenseCategory | "all");
      setCurrentPage(1);
    };

    // Handle pagination
    const handlePageChange = (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
        loadExpenses(page);
      }
    };

    // Handle expense actions
    const handleSubmitExpense = async (expense: Expense) => {
      try {
        await expenseService.submitExpense(expense.id, {});
        toast({
          title: "Success",
          description: "Expense submitted for approval",
        });
        loadExpenses(currentPage);
      } catch (error: any) {
        console.error("Error submitting expense:", error);
        toast({
          title: "Error",
          description: error.message || "Failed to submit expense",
          variant: "destructive",
        });
      }
    };

    const canSubmit = (expense: Expense) => {
      return expense.status === ExpenseStatus.DRAFT;
    };

    const canEdit = (expense: Expense) => {
      return expense.status === ExpenseStatus.DRAFT;
    };

    const getCategoryInfo = (category: ExpenseCategory) => {
      return EXPENSE_CATEGORY_INFO[category];
    };

    return (
      <Card className={className} ref={ref}>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle>My Expenses</CardTitle>
            {onCreateExpense && (
              <Button
                onClick={onCreateExpense}
                className="gap-2"
                data-testid="create-expense-button"
              >
                <Plus className="h-4 w-4" />
                Create Expense
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search expenses..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Status Filter */}
            <Select
              value={statusFilter}
              onValueChange={handleStatusFilter}
              disabled={statusFilters && statusFilters.length > 0}
            >
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.values(ExpenseStatus).map((status) => (
                  <SelectItem key={status} value={status}>
                    {status.replace("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Category Filter */}
            <Select value={categoryFilter} onValueChange={handleCategoryFilter}>
              <SelectTrigger className="w-full sm:w-[180px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.values(ExpenseCategory).map((category) => {
                  const categoryInfo = getCategoryInfo(category);
                  return (
                    <SelectItem key={category} value={category}>
                      <div className="flex items-center gap-2">
                        <span>{categoryInfo.icon}</span>
                        <span>{categoryInfo.label}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {/* Results Summary */}
          {!loading && (
            <div className="text-sm text-muted-foreground mb-4">
              Showing {expenses.length} of {totalCount} expenses
            </div>
          )}

          {/* External Filter Indicator */}
          {statusFilters && statusFilters.length > 0 && (
            <div className="flex items-center gap-2 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <div className="flex items-center gap-2 flex-1">
                <span className="text-sm font-medium text-blue-900">
                  Filtered by status:
                </span>
                {statusFilters.map((status) => (
                  <Badge
                    key={status}
                    variant="secondary"
                    className="bg-blue-100 text-blue-800"
                  >
                    {status.replace("_", " ")}
                  </Badge>
                ))}
              </div>
              {onClearFilters && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onClearFilters}
                  className="text-blue-700 border-blue-300 hover:bg-blue-100"
                >
                  Clear Filter
                </Button>
              )}
            </div>
          )}

          {/* Desktop Table */}
          <div className="hidden lg:block rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  // Loading rows
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="h-4 bg-muted rounded animate-pulse" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 bg-muted rounded animate-pulse" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 bg-muted rounded animate-pulse" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 bg-muted rounded animate-pulse" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 bg-muted rounded animate-pulse" />
                      </TableCell>
                      <TableCell>
                        <div className="h-4 bg-muted rounded animate-pulse" />
                      </TableCell>
                    </TableRow>
                  ))
                ) : expenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">
                      <div className="text-muted-foreground">
                        {searchTerm ||
                        statusFilter !== "all" ||
                        categoryFilter !== "all"
                          ? "No expenses match your filters"
                          : "No expenses yet"}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  expenses.map((expense) => {
                    const categoryInfo = getCategoryInfo(expense.category);
                    return (
                      <TableRow
                        key={expense.id}
                        data-testid={`expense-item-${expense.id}`}
                      >
                        <TableCell className="font-medium">
                          <LocalDate date={expense.expenseDate} />
                        </TableCell>
                        <TableCell>
                          <div
                            className="max-w-xs truncate"
                            title={expense.description}
                          >
                            {expense.description}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-base" aria-hidden="true">
                              {categoryInfo.icon}
                            </span>
                            <span className="text-sm">
                              {categoryInfo.label}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          <Currency amount={parseFloat(expense.amount)} />
                        </TableCell>
                        <TableCell>
                          <ExpenseStatusBadge
                            status={expense.status}
                            size="sm"
                          />
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <TooltipProvider>
                              {onViewExpense && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => onViewExpense(expense)}
                                      className="h-8 w-8 p-0"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>View details</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {canEdit(expense) && onEditExpense && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => onEditExpense(expense)}
                                      className="h-8 w-8 p-0"
                                    >
                                      <Edit className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Edit expense</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                              {canSubmit(expense) && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() =>
                                        handleSubmitExpense(expense)
                                      }
                                      className="h-8 w-8 p-0"
                                    >
                                      <Send className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Submit for approval</p>
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </TooltipProvider>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>

          {/* Mobile/Tablet Cards */}
          <div className="lg:hidden space-y-3">
            {loading ? (
              // Loading cards
              Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="border rounded-md p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <div className="h-4 bg-muted rounded animate-pulse w-3/4" />
                      <div className="h-6 bg-muted rounded animate-pulse w-16" />
                    </div>
                    <div className="h-3 bg-muted rounded animate-pulse w-1/2" />
                    <div className="h-3 bg-muted rounded animate-pulse w-1/4" />
                  </div>
                </div>
              ))
            ) : expenses.length === 0 ? (
              <div className="text-center py-8 border rounded-md">
                <div className="text-muted-foreground">
                  {searchTerm ||
                  statusFilter !== "all" ||
                  categoryFilter !== "all"
                    ? "No expenses match your filters"
                    : "No expenses yet"}
                </div>
                {onCreateExpense &&
                  !searchTerm &&
                  statusFilter === "all" &&
                  categoryFilter === "all" && (
                    <Button
                      variant="outline"
                      onClick={onCreateExpense}
                      className="mt-2 gap-2"
                      data-testid="create-first-expense-button"
                    >
                      <Plus className="h-4 w-4" />
                      Create your first expense
                    </Button>
                  )}
              </div>
            ) : (
              expenses.map((expense) => (
                <MobileExpenseCard
                  key={expense.id}
                  expense={expense}
                  onView={onViewExpense}
                  onEdit={onEditExpense}
                  onSubmit={handleSubmitExpense}
                />
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-2 py-4">
              <div className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage <= 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage >= totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  },
);

export default ContractorExpenseList;
